import { ChangeDetectorRef, Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { MatTable } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { redAnimations } from '@red/animations';
import { PaginationAdapter } from '@red/data-access';
import { ITableActionButton } from '@shared/components/table-action-group';
import { ToastService } from '@shared/components/toast';
import { AbstractList, SearchFormKeyConfig } from '@shared/core';
import { IQuerySearch } from '@shared/data-access/interfaces';
import { PermissionsService } from '@shared/permission';
import { Observable, of } from 'rxjs';
import { format } from 'date-fns';
import { DYNAMIC_REPORT_HTTP_LOADER, DynamicReportHttpLoader } from '../dynamic-report-http-loader.service';
import { DynamicReportGeneralModel } from '../dynamic-report-general.model';
import { Confirmable } from '@shared/components/confirm-dialog';
import * as saveAs from 'file-saver';
import { DynamicReportGeneralDto } from '../dynamic-report-general.dto';

@Component({
    selector: 'red-dynamic-report-general-list',
    templateUrl: './dynamic-report-general-list.component.html',
    styleUrls: ['./dynamic-report-general-list.component.scss'],
    animations: redAnimations,
    providers: []
})
export class DynamicReportGeneralListComponent extends AbstractList<DynamicReportGeneralModel> implements OnInit {
    @ViewChild(MatTable) table!: MatTable<UntypedFormGroup>;
    @ViewChild('matDrawer', { static: true }) matDrawer!: MatDrawer;

    @Input() reportFeatureName = 'dynamic_report'; // Removed type annotation, e.g., 'project_dynamic_report'

    formFilter!: UntypedFormGroup;
    searchCtrl = new UntypedFormControl();
    drawerMode: 'side' | 'over' | 'push' = 'over';
    columns: { columnDef: string; header: string; cell: (item: DynamicReportGeneralModel, index?: number) => any; templateRef?: string }[] = [
        { columnDef: 'name', header: 'Report Name', cell: (item: DynamicReportGeneralModel) => item.name },
        { columnDef: 'createdBy', header: 'Created By', cell: (item: DynamicReportGeneralModel) => item.createdBy?.name },
        { columnDef: 'createdAt', header: 'Created On', cell: (item: DynamicReportGeneralModel) => item?.createdAt ? format(new Date(item?.createdAt), 'dd MMM yyyy hh:mm a') : '' },
        { columnDef: 'updatedBy', header: 'Last Updated By', cell: (item: DynamicReportGeneralModel) => `${item.updatedBy?.name ?? '-'} ${item?.updatedAt ? ' on ' + format(new Date(item?.updatedAt), 'dd MMM yyyy hh:mm a') : ''}` },

    ];
    displayedColumns: string[] = [];
    get viewPermissionSlug(): string {
        return `${this.permissionPrefix}:${this.reportFeatureName}:read`;
    }

    permissionPrefix = this._activatedRoute.snapshot.data['permission']?.prefix ?? '';

    buttons: ITableActionButton[] = [
        {
            id: 'download',
            icon: 'download',
            label: 'Download',
            hidden: (item, action) => this.isItemHidden(item, action),
            permissionSlug: '',
        },
        {
            id: 'update',
            icon: 'edit',
            label: 'Edit',
            hidden: (item, action) => this.isItemHidden(item, action),
            permissionSlug: '',
            link: (item, action) => `/dynamic-report/${item.reportType}/edit/${item.id}`,
            linkQueryParams: (item, action) => ({ reportType: item.reportType }),
            queryParamsHandling: 'merge',
        },
        {
            id: 'delete',
            icon: 'delete',
            label: 'Delete',
            hidden: (item, action) => this.isItemHidden(item, action),
            permissionSlug: '',
        },
    ];

    constructor(
        @Inject(DYNAMIC_REPORT_HTTP_LOADER) private _apiService: DynamicReportHttpLoader,

        protected override _router: Router,
        protected override _cdr: ChangeDetectorRef,
        protected override _activatedRoute: ActivatedRoute,
        protected override _formBuilder: UntypedFormBuilder,
        private _dialog: MatDialog,
        private _toastService: ToastService,
        private _permissionService: PermissionsService,

    ) {
        super(_formBuilder, _activatedRoute, _router, _cdr);
        this.displayedColumns.push('sn');
        this.displayedColumns.push(...this.columns.map(col => col.columnDef));
        this.displayedColumns.push('action');

    }

    override ngOnInit(): void {
        super.ngOnInit();
        //Update button permission dynamically
        this.buttons = this.buttons.map(btn => ({
            ...btn,
            permissionSlug: (this.permissionPrefix && btn.id) ? `${this.permissionPrefix}:${btn.id}` : undefined
        }));
    }


    override get searchFormDefaults(): IQuerySearch {
        return {
            orderBy: 'updatedAt',
            orderType: 'desc',
            // createdAtFrom: '',
            // createdAtTo: '',
            name: '',
            // createdBy: '',
        };
    }

    // override get searchFormDefaultConfig(): SearchFormKeyConfig {
    //     return {
    //         createdAtFrom: { valueType: 'number' },
    //         createdAtTo: { valueType: 'number' },
    //     };
    // }

    getItems(filters: IQuerySearch): Observable<PaginationAdapter<DynamicReportGeneralModel>> {
        filters['name'] = filters['key'] ?? undefined;
        return this._apiService.search(filters, { removeEmpty: { enable: true } });
    }

    onChangePage(page: number): void {
        this.changePage(page);
    }

    onItemsPerPageChange(limit: number): void {
        this.changeItemPerPage(limit);
    }

    actionClick(action: ITableActionButton, item: DynamicReportGeneralModel): void {
        switch (action.id) {
            case 'download':
                this.download(item)
                break;
            case 'delete':
                this.delete(item)
                break;
        }
    }

    isItemHidden(item: DynamicReportGeneralModel, action?: ITableActionButton): boolean {
        let isHidden = false;
        const permissionSlug = action?.permissionSlug;
        isHidden = permissionSlug ? this._permissionService.canDeny(permissionSlug) : false;
        return isHidden;
    }

    identify(index: number, item: any) {
        return item.id;
    }

    @Confirmable({
        type: 'warning',
        title: 'Delete Report!',
        description: 'Are you sure to delete this report?',
    })
    delete(item: DynamicReportGeneralModel) {
        this._apiService.delete(item.id).subscribe({
            next: result => {
                this._toastService.open({ type: 'success', title: 'Success', messages: 'Report deleted successfully!' });
                this.refresh();
            },
            error: err => {
                this._toastService.open({
                    type: 'danger',
                    title: 'Error',
                    messages: err.message ?? 'Error occurred while deleting report!',
                });
            },
        });
    };

    download(item: DynamicReportGeneralModel): void {
        const payload = DynamicReportGeneralDto.fromJson(item);
        this.handleDisabledButtonDownload(true);
        this._apiService.export(item?.id, payload).subscribe({
            next: (res) => {
                const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                saveAs(blob, `${item.name}.xlsx`);
                this.handleDisabledButtonDownload(false);

            },
            error: (error) => {
                this.handleDisabledButtonDownload(false);
                this._toastService.open({
                    type: 'danger',
                    title: 'Error',
                    messages: error.message ?? 'Something went wrong',
                });
            }
        });
    }

    handleDisabledButtonDownload(isDisabled: boolean) {
        this.buttons = this.buttons.map(btn => {
            return { ...btn, disabled: btn.id === 'download' ? () => isDisabled : undefined }; // Update the button to be disabled or not
        })
    }
}